// src/admin/pages/NewBooking.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { EmptyState } from "../ui/EmptyState";

// ---------------- helpers ----------------
function ymd(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalize(s) {
  return String(s || "").toLowerCase().trim();
}

function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;

  const aS = new Date(aStart).getTime();
  const aE = new Date(aEnd).getTime();
  const bS = new Date(bStart).getTime();
  const bE = new Date(bEnd).getTime();

  return aS <= bE && bS <= aE;
}

function daysBetween(startISO, endISO) {
  if (!startISO || !endISO) return null;

  const a = new Date(startISO);
  const b = new Date(endISO);

  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return null;
  }

  const ms = b.getTime() - a.getTime();
  const raw = Math.round(ms / (1000 * 60 * 60 * 24));

  return raw === 0 ? 1 : raw;
}

function formatMoney(n) {
  const value = Number(n || 0);
  return `$${value.toFixed(2)}`;
}

async function ensureProfileRowForCurrentUser() {
  const { data: auth, error: authErr } = await supabase.auth.getUser();

  if (authErr) throw authErr;

  const user = auth?.user;

  if (!user?.id) return null;

  const payload = {
    id: user.id,
    email: user.email ?? null,
  };

  const { data, error } = await supabase
    .from("profiles")
    .upsert(payload, { onConflict: "id" })
    .select("id")
    .single();

  if (error) throw error;

  return data?.id || user.id;
}

// ---------------- delivery pricing ----------------
const SHOP_ADDRESS = "99 Montclair Dr, Woodstock Ontario, N4V 1C5";

const DELIVERY_RULES = {
  Explore: {
    freeTrips: 0,
    includedKm: 0,
    ratePerKm: 2,
  },
  Adventure: {
    freeTrips: 4,
    includedKm: 50,
    ratePerKm: 2,
  },
  Elite: {
    freeTrips: 8,
    includedKm: 100,
    ratePerKm: 2,
  },
  Platinum: {
    freeTrips: 8,
    includedKm: 100,
    ratePerKm: 2,
  },
};

function getDeliveryRule(tier) {
  return (
    DELIVERY_RULES[tier] || {
      freeTrips: 0,
      includedKm: 0,
      ratePerKm: 2,
    }
  );
}

// ---------------- component ----------------
export default function NewBooking() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const [selectedMembershipId, setSelectedMembershipId] = useState(
    sp.get("membershipId") || ""
  );

  const [memberQuery, setMemberQuery] = useState("");

  // data
  const [memberships, setMemberships] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [globalApprovedActive, setGlobalApprovedActive] = useState([]);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  // booking fields
  const [vehicleId, setVehicleId] = useState("");
  const [startDate, setStartDate] = useState(ymd(new Date()));
  const [endDate, setEndDate] = useState(ymd(new Date()));
  const [status, setStatus] = useState("approved");

  // pickup | delivery
  const [serviceType, setServiceType] = useState("pickup");
  const [pickupLocation, setPickupLocation] = useState(SHOP_ADDRESS);
  const [dropoffLocation, setDropoffLocation] = useState("");

  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [outboundKm, setOutboundKm] = useState("");
  const [returnKm, setReturnKm] = useState("");
  const [deliveryFeeCalculated, setDeliveryFeeCalculated] = useState(false);

  const [concierge, setConcierge] = useState("Alex");
  const [notes, setNotes] = useState("");

  // email
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);

  // submit state
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [saveOk, setSaveOk] = useState(false);

  // Load memberships + vehicles + global bookings + delivery usage
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadErr(null);

      try {
        const [
          { data: memData, error: memErr },
          { data: vehData, error: vehErr },
          { data: bksData, error: bErr },
          { data: deliveryData, error: deliveryErr },
        ] = await Promise.all([
          supabase
            .from("memberships")
            .select(
              `
                id,
                created_at,
                status,
                tier,
                billing,
                days_remaining,
                can_book,
                age_verified,
                start_date,
                renewal_date,
                contact_id,
                profile_id,
                crm_contacts:contact_id (
                  id,
                  first_name,
                  last_name,
                  email,
                  phone,
                  stage
                )
              `
            )
            .order("created_at", { ascending: false })
            .limit(500),

          supabase
            .from("vehicles")
            .select(
              "id, name, category, status, location, tier_access, sub_category"
            )
            .order("created_at", { ascending: false })
            .limit(500),

          supabase
            .from("bookings")
            .select(
              "id, membership_id, vehicle_id, start_date, end_date, status"
            )
            .in("status", ["approved", "active"])
            .order("created_at", { ascending: false })
            .limit(500),

          supabase
            .from("booking_delivery")
            .select(
              `
                booking_id,
                service_type,
                free_trip_used,
                outbound_trip_used,
                return_trip_used,
                trips_used,
                delivery_fee,
                outbound_fee,
                return_fee,
                bookings!inner (
                  id,
                  membership_id,
                  status
                )
              `
            )
            .eq("service_type", "delivery")
            .limit(1000),
        ]);

        if (memErr) throw memErr;
        if (vehErr) throw vehErr;
        if (bErr) throw bErr;
        if (deliveryErr) throw deliveryErr;

        if (!cancelled) {
          setMemberships(memData || []);
          setVehicles(vehData || []);
          setGlobalApprovedActive(bksData || []);
          setDeliveryHistory(deliveryData || []);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadErr(e.message || "Failed to load booking data.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedMembership = useMemo(() => {
    if (!selectedMembershipId) return null;

    return (
      memberships.find((m) => m.id === selectedMembershipId) || null
    );
  }, [memberships, selectedMembershipId]);

  const selectedContact = selectedMembership?.crm_contacts || null;

  const selectedVehicle = useMemo(() => {
    if (!vehicleId) return null;

    return vehicles.find((vehicle) => vehicle.id === vehicleId) || null;
  }, [vehicles, vehicleId]);

  // Search results
  const memberResults = useMemo(() => {
    const q = normalize(memberQuery);

    if (!q) return [];

    return memberships
      .filter((m) => {
        const c = m.crm_contacts || {};

        const hay = normalize(
          `${c.first_name || ""} ${c.last_name || ""} ${
            c.email || ""
          } ${c.phone || ""} ${m.tier || ""} ${m.status || ""}`
        );

        return hay.includes(q);
      })
      .slice(0, 12);
  }, [memberships, memberQuery]);

  // Eligible vehicles
  const eligibleVehicles = useMemo(() => {
    if (!selectedMembership) return [];

    const tier = selectedMembership.tier;

    return vehicles.filter(
      (v) =>
        Array.isArray(v.tier_access) && v.tier_access.includes(tier)
    );
  }, [vehicles, selectedMembership]);

  const eligibleAvailableFirst = useMemo(() => {
    const availableVehicles = eligibleVehicles.filter(
      (v) => v.status === "available"
    );

    return availableVehicles.length
      ? availableVehicles
      : eligibleVehicles;
  }, [eligibleVehicles]);

  useEffect(() => {
    if (!selectedMembership) {
      setVehicleId("");
      return;
    }

    const currentVehicleIsEligible = eligibleAvailableFirst.some(
      (vehicle) => vehicle.id === vehicleId
    );

    if (!currentVehicleIsEligible) {
      setVehicleId(eligibleAvailableFirst?.[0]?.id || "");
    }

    setDropoffLocation((prev) => (prev ? prev : pickupLocation));
  }, [
    selectedMembership,
    eligibleAvailableFirst,
    pickupLocation,
    vehicleId,
  ]);

  useEffect(() => {
    if (!selectedContact?.email) {
      setSendConfirmationEmail(false);
      setEmailStatus(null);
      return;
    }

    setSendConfirmationEmail(true);
    setEmailStatus(null);
  }, [selectedContact?.id, selectedContact?.email]);

  // Conflict check
  const conflictInfo = useMemo(() => {
    if (!vehicleId || !startDate || !endDate) {
      return {
        conflict: false,
        blockers: [],
      };
    }

    const blockers = globalApprovedActive.filter(
      (b) =>
        b.vehicle_id === vehicleId &&
        overlaps(startDate, endDate, b.start_date, b.end_date)
    );

    return {
      conflict: blockers.length > 0,
      blockers,
    };
  }, [globalApprovedActive, vehicleId, startDate, endDate]);

  const days = useMemo(() => {
    if (!startDate || !endDate) return null;

    return daysBetween(startDate, endDate);
  }, [startDate, endDate]);

  const canBook = !!selectedMembership?.can_book;

  const deliveryRule = useMemo(() => {
    return getDeliveryRule(selectedMembership?.tier);
  }, [selectedMembership?.tier]);

  const usedFreeTrips = useMemo(() => {
    if (!selectedMembershipId) return 0;

    return (deliveryHistory || []).filter((row) => {
      const booking = row?.bookings || null;
      const bookingStatus = String(
        booking?.status || ""
      ).toLowerCase();

      return (
        booking?.membership_id === selectedMembershipId &&
        row?.service_type === "delivery" &&
        row?.free_trip_used === true &&
        bookingStatus !== "cancelled"
      );
    }).length;
  }, [deliveryHistory, selectedMembershipId]);

  const remainingFreeTrips = useMemo(() => {
    return Math.max(
      0,
      Number(deliveryRule.freeTrips || 0) - usedFreeTrips
    );
  }, [deliveryRule.freeTrips, usedFreeTrips]);

  const deliveryQuote = useMemo(() => {
    if (serviceType !== "delivery") {
      return {
        enabled: false,
        includedKm: 0,
        ratePerKm: 2,
        freeTripsRemaining: remainingFreeTrips,
        outboundTripUsed: false,
        returnTripUsed: false,
        tripsUsed: 0,
        freeTripUsed: false,
        outboundDistanceKm: 0,
        returnDistanceKm: 0,
        outboundBillableKm: 0,
        returnBillableKm: 0,
        outboundFee: 0,
        returnFee: 0,
        deliveryFee: 0,
      };
    }

    const includedKm = Number(deliveryRule.includedKm || 0);
    const ratePerKm = Number(deliveryRule.ratePerKm || 2);

    const outboundDistanceKm = Number(outboundKm || 0);
    const returnDistanceKm = Number(returnKm || 0);

    const outboundTripUsed = outboundDistanceKm > 0;
    const returnTripUsed = returnDistanceKm > 0;

    const tripsUsed =
      (outboundTripUsed ? 1 : 0) + (returnTripUsed ? 1 : 0);

    const freeTripUsed =
      tripsUsed > 0 && remainingFreeTrips > 0;

    const outboundBillableKm = outboundTripUsed
      ? Math.max(0, outboundDistanceKm - includedKm)
      : 0;

    const returnBillableKm = returnTripUsed
      ? Math.max(0, returnDistanceKm - includedKm)
      : 0;

    const outboundFee = outboundBillableKm * ratePerKm;
    const returnFee = returnBillableKm * ratePerKm;
    const deliveryFee = outboundFee + returnFee;

    return {
      enabled: true,
      includedKm,
      ratePerKm,
      freeTripsRemaining: remainingFreeTrips,
      outboundTripUsed,
      returnTripUsed,
      tripsUsed,
      freeTripUsed,
      outboundDistanceKm,
      returnDistanceKm,
      outboundBillableKm,
      returnBillableKm,
      outboundFee,
      returnFee,
      deliveryFee,
    };
  }, [
    serviceType,
    outboundKm,
    returnKm,
    deliveryRule,
    remainingFreeTrips,
  ]);

  function handleCalculateDelivery() {
    if (serviceType !== "delivery") return;

    const outbound = Number(outboundKm || 0);
    const ret = Number(returnKm || 0);

    const outboundValid =
      Number.isFinite(outbound) && outbound >= 0;

    const returnValid =
      Number.isFinite(ret) && ret >= 0;

    if (!outboundValid || !returnValid) {
      setSaveErr(
        "Enter valid outbound and return distances in km."
      );
      setDeliveryFeeCalculated(false);
      return;
    }

    if (outbound <= 0 && ret <= 0) {
      setSaveErr(
        "Enter at least one delivery distance greater than 0 km."
      );
      setDeliveryFeeCalculated(false);
      return;
    }

    setSaveErr(null);
    setDeliveryFeeCalculated(true);
  }

  async function sendBookingConfirmation(bookingId) {
    const customerEmail = selectedContact?.email?.trim();

    if (!sendConfirmationEmail) {
      return {
        success: true,
        sent: false,
        skipped: true,
      };
    }

    if (!customerEmail) {
      throw new Error(
        "The booking was created, but the customer does not have an email address."
      );
    }

    const { data, error } = await supabase.functions.invoke(
      "send-booking-confirmation",
      {
        body: {
          bookingId,
          customer: {
            firstName: selectedContact?.first_name || "",
            lastName: selectedContact?.last_name || "",
            email: customerEmail,
          },
          membership: {
            id: selectedMembership?.id || null,
            tier: selectedMembership?.tier || "",
          },
          booking: {
            vehicleId: selectedVehicle?.id || vehicleId,
            vehicleName:
              selectedVehicle?.name || "Reserved vehicle",
            vehicleCategory:
              selectedVehicle?.category || "",
            vehicleSubCategory:
              selectedVehicle?.sub_category || "",
            startDate,
            endDate,
            status: String(
              status || "approved"
            ).toLowerCase(),
            concierge: concierge?.trim() || null,
            notes: notes?.trim() || null,
            serviceType,
            pickupLocation:
              pickupLocation?.trim() || null,
            dropoffLocation:
              serviceType === "delivery"
                ? deliveryAddress.trim()
                : dropoffLocation?.trim() || null,
            deliveryAddress:
              serviceType === "delivery"
                ? deliveryAddress.trim()
                : null,
            deliveryFee:
              serviceType === "delivery"
                ? Number(deliveryQuote.deliveryFee || 0)
                : 0,
            outboundDistanceKm:
              serviceType === "delivery"
                ? Number(
                    deliveryQuote.outboundDistanceKm || 0
                  )
                : 0,
            returnDistanceKm:
              serviceType === "delivery"
                ? Number(
                    deliveryQuote.returnDistanceKm || 0
                  )
                : 0,
          },
        },
      }
    );

    if (error) {
      throw new Error(
        error.message ||
          "The confirmation email could not be sent."
      );
    }

    if (!data?.success) {
      throw new Error(
        data?.error ||
          "The email service did not confirm delivery."
      );
    }

    return data;
  }

  async function submit() {
    setSaveErr(null);
    setSaveOk(false);
    setEmailStatus(null);

    if (!selectedMembership) {
      setSaveErr("Select a member.");
      return;
    }

    if (!vehicleId) {
      setSaveErr("Select a vehicle.");
      return;
    }

    if (!startDate || !endDate) {
      setSaveErr("Choose start and end dates.");
      return;
    }

    if (days === null || days < 1) {
      setSaveErr(
        "The end date must be the same as or later than the start date."
      );
      return;
    }

    if (!canBook) {
      setSaveErr(
        "This member cannot book (can_book=false)."
      );
      return;
    }

    if (conflictInfo.conflict) {
      setSaveErr(
        "Conflict detected with an approved/active booking. Choose different dates."
      );
      return;
    }

    if (
      sendConfirmationEmail &&
      !selectedContact?.email?.trim()
    ) {
      setSaveErr(
        "Automatic email is enabled, but this customer does not have an email address."
      );
      return;
    }

    if (serviceType === "delivery") {
      if (!deliveryAddress.trim()) {
        setSaveErr("Enter a delivery address.");
        return;
      }

      if (!deliveryFeeCalculated) {
        setSaveErr(
          "Please calculate the delivery fee before creating the booking."
        );
        return;
      }
    }

    setSaving(true);

    let createdBookingId = null;

    try {
      const createdById =
        await ensureProfileRowForCurrentUser();

      const bookingPayload = {
        membership_id: selectedMembership.id,
        profile_id:
          selectedMembership.profile_id || null,
        created_by: createdById || null,
        vehicle_id: vehicleId,
        start_date: startDate,
        end_date: endDate,
        status: String(
          status || "approved"
        ).toLowerCase(),
        pickup_location: pickupLocation?.trim()
          ? pickupLocation.trim()
          : null,
        dropoff_location:
          serviceType === "delivery"
            ? deliveryAddress.trim()
            : dropoffLocation?.trim()
            ? dropoffLocation.trim()
            : null,
        concierge: concierge?.trim()
          ? concierge.trim()
          : null,
        notes: notes?.trim()
          ? notes.trim()
          : null,
      };

      const { data: bookingRow, error: bookingErr } =
        await supabase
          .from("bookings")
          .insert(bookingPayload)
          .select("id")
          .single();

      if (bookingErr) throw bookingErr;

      if (!bookingRow?.id) {
        throw new Error(
          "Booking created but no booking id was returned."
        );
      }

      createdBookingId = bookingRow.id;

      const deliveryPayload = {
        booking_id: bookingRow.id,
        service_type: serviceType,
        delivery_address:
          serviceType === "delivery"
            ? deliveryAddress.trim()
            : null,

        free_trip_used:
          serviceType === "delivery"
            ? !!deliveryQuote.freeTripUsed
            : false,

        trips_used:
          serviceType === "delivery"
            ? Number(deliveryQuote.tripsUsed || 0)
            : 0,

        outbound_trip_used:
          serviceType === "delivery"
            ? !!deliveryQuote.outboundTripUsed
            : false,

        return_trip_used:
          serviceType === "delivery"
            ? !!deliveryQuote.returnTripUsed
            : false,

        outbound_distance_km:
          serviceType === "delivery" &&
          deliveryQuote.outboundTripUsed
            ? Number(
                deliveryQuote.outboundDistanceKm || 0
              )
            : null,

        return_distance_km:
          serviceType === "delivery" &&
          deliveryQuote.returnTripUsed
            ? Number(
                deliveryQuote.returnDistanceKm || 0
              )
            : null,

        distance_km:
          serviceType === "delivery"
            ? Number(
                deliveryQuote.outboundDistanceKm || 0
              ) +
              Number(
                deliveryQuote.returnDistanceKm || 0
              )
            : null,

        included_km:
          serviceType === "delivery"
            ? Number(deliveryQuote.includedKm || 0)
            : 0,

        outbound_billable_km:
          serviceType === "delivery"
            ? Number(
                deliveryQuote.outboundBillableKm || 0
              )
            : 0,

        return_billable_km:
          serviceType === "delivery"
            ? Number(
                deliveryQuote.returnBillableKm || 0
              )
            : 0,

        billable_km:
          serviceType === "delivery"
            ? Number(
                deliveryQuote.outboundBillableKm || 0
              ) +
              Number(
                deliveryQuote.returnBillableKm || 0
              )
            : 0,

        rate_per_km:
          serviceType === "delivery"
            ? Number(deliveryQuote.ratePerKm || 2)
            : 2,

        outbound_fee:
          serviceType === "delivery"
            ? Number(deliveryQuote.outboundFee || 0)
            : 0,

        return_fee:
          serviceType === "delivery"
            ? Number(deliveryQuote.returnFee || 0)
            : 0,

        delivery_fee:
          serviceType === "delivery"
            ? Number(deliveryQuote.deliveryFee || 0)
            : 0,
      };

      const { error: deliveryErr } = await supabase
        .from("booking_delivery")
        .insert(deliveryPayload);

      if (deliveryErr) throw deliveryErr;

      setSaveOk(true);

      if (sendConfirmationEmail) {
        setEmailStatus({
          tone: "info",
          message:
            "Booking created. Sending confirmation email…",
        });

        try {
          await sendBookingConfirmation(bookingRow.id);

          setEmailStatus({
            tone: "success",
            message: `Confirmation email sent to ${selectedContact.email}.`,
          });

          navigate("/admin/bookings", {
            state: {
              notice: `Booking created and confirmation email sent to ${selectedContact.email}.`,
            },
          });
        } catch (emailError) {
          console.error(
            "Booking confirmation email failed:",
            emailError
          );

          const emailMessage =
            emailError?.message ||
            "The confirmation email could not be sent.";

          setEmailStatus({
            tone: "warning",
            message: `Booking created, but the email failed: ${emailMessage}`,
          });

          setSaveErr(
            `The booking was created successfully, but the confirmation email failed: ${emailMessage}`
          );
        }
      } else {
        navigate("/admin/bookings", {
          state: {
            notice:
              "Booking created. Confirmation email was not sent.",
          },
        });
      }
    } catch (e) {
      const defaultMessage = createdBookingId
        ? "The booking was created, but an additional booking step failed."
        : "Failed to create booking.";

      setSaveErr(e.message || defaultMessage);
    } finally {
      setSaving(false);
    }
  }

  const selectedName = selectedContact
    ? fullNameFromContact(selectedContact)
    : "—";

  const emailStatusStyle =
    emailStatus?.tone === "success"
      ? {
          background: "rgba(34, 197, 94, 0.12)",
          border: "1px solid rgba(34, 197, 94, 0.35)",
          color: "inherit",
          borderRadius: 8,
          padding: 12,
        }
      : emailStatus?.tone === "warning"
      ? {
          background: "rgba(245, 158, 11, 0.12)",
          border: "1px solid rgba(245, 158, 11, 0.35)",
          color: "inherit",
          borderRadius: 8,
          padding: 12,
        }
      : {
          background: "rgba(59, 130, 246, 0.1)",
          border: "1px solid rgba(59, 130, 246, 0.3)",
          color: "inherit",
          borderRadius: 8,
          padding: 12,
        };

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <div className="page__back">
            <Link className="link" to="/admin/bookings">
              ← Bookings
            </Link>
          </div>

          <h1 className="page__title">New Booking</h1>

          <div className="page__sub">
            Create a booking for a member and optionally send
            an automatic confirmation email.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          {saveOk ? (
            <Badge tone="success">Saved</Badge>
          ) : null}

          {saving ? (
            <Badge tone="info">Saving…</Badge>
          ) : null}
        </div>
      </div>

      {loading && (
        <div className="memMuted">
          Loading members, vehicles, and bookings…
        </div>
      )}

      {loadErr && (
        <div
          className="adminAuth__error"
          style={{ marginTop: 10 }}
        >
          {loadErr}
        </div>
      )}

      <div className="grid grid--2">
        <Card
          title="Member"
          subtitle="Search and select the customer"
        >
          <div className="memLabel">Search</div>

          <input
            className="memInput"
            value={memberQuery}
            onChange={(e) => setMemberQuery(e.target.value)}
            placeholder="Type name, email, phone…"
            disabled={loading}
          />

          {!!memberQuery && (
            <div
              className="memResults"
              style={{ marginTop: 10 }}
            >
              {memberResults.map((m) => {
                const c = m.crm_contacts || null;
                const name = fullNameFromContact(c);

                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`memResult ${
                      m.id === selectedMembershipId
                        ? "memResult--active"
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedMembershipId(m.id);
                      setMemberQuery("");
                      setSaveErr(null);
                      setSaveOk(false);
                      setEmailStatus(null);
                    }}
                  >
                    <div className="memResult__main">
                      <div className="memResult__name">
                        {name}{" "}
                        <span className="memResult__meta">
                          • {c?.email || "—"}
                        </span>
                      </div>

                      <div className="memResult__sub">
                        {c?.phone || "—"} • Tier: {m.tier} •
                        Status: {m.status}
                      </div>
                    </div>

                    <div className="memResult__right">
                      <Badge tone="info">{m.tier}</Badge>

                      <Badge
                        tone={
                          String(m.status).toLowerCase() ===
                          "active"
                            ? "success"
                            : "warning"
                        }
                      >
                        {m.status}
                      </Badge>
                    </div>
                  </button>
                );
              })}

              {!memberResults.length && (
                <div className="memMuted">
                  No matches.
                </div>
              )}
            </div>
          )}

          {!selectedMembership ? (
            <EmptyState
              title="No member selected"
              hint="Search above and click a result."
            />
          ) : (
            <div style={{ marginTop: 12 }}>
              <div className="memCustomerName">
                {selectedName}
              </div>

              <div className="memMuted">
                {selectedContact?.email || "—"} •{" "}
                {selectedContact?.phone || "—"}
              </div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Badge tone="info">
                  {selectedMembership.tier}
                </Badge>

                <Badge
                  tone={
                    String(
                      selectedMembership.status
                    ).toLowerCase() === "active"
                      ? "success"
                      : "warning"
                  }
                >
                  {selectedMembership.status}
                </Badge>

                <Badge
                  tone={
                    selectedMembership.can_book
                      ? "success"
                      : "warning"
                  }
                >
                  can_book:{" "}
                  {selectedMembership.can_book
                    ? "true"
                    : "false"}
                </Badge>

                <Badge
                  tone={
                    selectedMembership.profile_id
                      ? "success"
                      : "neutral"
                  }
                >
                  portal profile:{" "}
                  {selectedMembership.profile_id
                    ? "linked"
                    : "none (ok)"}
                </Badge>
              </div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Badge tone="neutral">
                  Free trips used: {usedFreeTrips}
                </Badge>

                <Badge tone="neutral">
                  Free trips remaining:{" "}
                  {remainingFreeTrips}
                </Badge>
              </div>

              {!canBook && (
                <div
                  className="adminAuth__error"
                  style={{ marginTop: 12 }}
                >
                  This member cannot book yet
                  (can_book=false).
                </div>
              )}

              {!selectedContact?.email && (
                <div
                  className="adminAuth__error"
                  style={{ marginTop: 12 }}
                >
                  This customer does not have an email
                  address. A confirmation email cannot be
                  sent.
                </div>
              )}
            </div>
          )}
        </Card>

        <Card
          title="Booking"
          subtitle="Vehicle + dates + delivery + conflict check"
        >
          {!selectedMembership ? (
            <div className="memMuted">
              Select a member to see eligible vehicles.
            </div>
          ) : (
            <>
              <div className="memFormRow">
                <div className="memLabel">
                  Vehicle (eligible for tier)
                </div>

                <select
                  className="memSelect"
                  value={vehicleId}
                  onChange={(e) =>
                    setVehicleId(e.target.value)
                  }
                  disabled={loading}
                >
                  {eligibleAvailableFirst.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                      {v.sub_category
                        ? ` • ${v.sub_category}`
                        : ""}{" "}
                      — {v.category} ({v.status})
                    </option>
                  ))}

                  {!eligibleAvailableFirst.length && (
                    <option value="">
                      No eligible vehicles
                    </option>
                  )}
                </select>
              </div>

              <div className="memFormRow memFormRow--2">
                <div>
                  <div className="memLabel">Start</div>

                  <input
                    className="memInput"
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setSaveErr(null);
                    }}
                  />
                </div>

                <div>
                  <div className="memLabel">End</div>

                  <input
                    className="memInput"
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setSaveErr(null);
                    }}
                  />
                </div>
              </div>

              <div className="memFormRow memFormRow--2">
                <div>
                  <div className="memLabel">Status</div>

                  <select
                    className="memSelect"
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value)
                    }
                  >
                    <option value="requested">
                      requested
                    </option>
                    <option value="approved">
                      approved
                    </option>
                    <option value="active">active</option>
                    <option value="completed">
                      completed
                    </option>
                    <option value="cancelled">
                      cancelled
                    </option>
                  </select>
                </div>

                <div>
                  <div className="memLabel">
                    Concierge
                  </div>

                  <input
                    className="memInput"
                    value={concierge}
                    onChange={(e) =>
                      setConcierge(e.target.value)
                    }
                    placeholder="Alex"
                  />
                </div>
              </div>

              <div className="memFormRow memFormRow--2">
                <div>
                  <div className="memLabel">
                    Service Type
                  </div>

                  <select
                    className="memSelect"
                    value={serviceType}
                    onChange={(e) => {
                      const next = e.target.value;

                      setServiceType(next);
                      setOutboundKm("");
                      setReturnKm("");
                      setDeliveryAddress("");
                      setDeliveryFeeCalculated(false);
                      setSaveErr(null);

                      if (next === "pickup") {
                        setDropoffLocation(
                          (prev) =>
                            prev || pickupLocation
                        );
                      }
                    }}
                  >
                    <option value="pickup">
                      Pickup
                    </option>
                    <option value="delivery">
                      Delivery
                    </option>
                  </select>
                </div>

                <div>
                  <div className="memLabel">
                    Pickup Location
                  </div>

                  <input
                    className="memInput"
                    value={pickupLocation}
                    onChange={(e) =>
                      setPickupLocation(e.target.value)
                    }
                    placeholder={SHOP_ADDRESS}
                  />
                </div>
              </div>

              {serviceType === "pickup" ? (
                <div className="memFormRow">
                  <div className="memLabel">
                    Drop-Off Location (optional)
                  </div>

                  <input
                    className="memInput"
                    value={dropoffLocation}
                    onChange={(e) =>
                      setDropoffLocation(e.target.value)
                    }
                    placeholder="Same as pickup or alternate location"
                  />
                </div>
              ) : (
                <>
                  <div className="memFormRow">
                    <div className="memLabel">
                      Delivery Address
                    </div>

                    <input
                      className="memInput"
                      value={deliveryAddress}
                      onChange={(e) => {
                        setDeliveryAddress(
                          e.target.value
                        );
                        setSaveErr(null);
                      }}
                      placeholder="Customer delivery address"
                    />
                  </div>

                  <div className="formRow2">
                    <div>
                      <div className="memLabel">
                        Outbound Distance (km)
                      </div>

                      <input
                        className="memInput"
                        type="number"
                        min="0"
                        step="0.1"
                        value={outboundKm}
                        onChange={(e) => {
                          setOutboundKm(e.target.value);
                          setDeliveryFeeCalculated(false);
                          setSaveErr(null);
                        }}
                        placeholder="Shop to customer"
                      />
                    </div>

                    <div>
                      <div className="memLabel">
                        Return Distance (km)
                      </div>

                      <input
                        className="memInput"
                        type="number"
                        min="0"
                        step="0.1"
                        value={returnKm}
                        onChange={(e) => {
                          setReturnKm(e.target.value);
                          setDeliveryFeeCalculated(false);
                          setSaveErr(null);
                        }}
                        placeholder="Customer back to shop"
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={
                        handleCalculateDelivery
                      }
                    >
                      Calculate Delivery Fee
                    </button>
                  </div>

                  <div
                    className="memInfoBlock"
                    style={{ marginTop: 12 }}
                  >
                    <div className="memBlockTitle">
                      Delivery Quote
                    </div>

                    <div className="memKV">
                      <span>Tier</span>
                      <div className="memKV__v">
                        {selectedMembership.tier}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>Free trips allowed</span>
                      <div className="memKV__v">
                        {deliveryRule.freeTrips}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>Free trips used</span>
                      <div className="memKV__v">
                        {usedFreeTrips}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        Free trips remaining
                      </span>
                      <div className="memKV__v">
                        {remainingFreeTrips}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        Trips used this booking
                      </span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? deliveryQuote.tripsUsed
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        This booking uses free trip
                      </span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? deliveryQuote.freeTripUsed
                            ? "Yes"
                            : "No"
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        Included km per trip
                      </span>
                      <div className="memKV__v">
                        {deliveryRule.includedKm} km
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        Outbound distance
                      </span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? `${Number(
                              deliveryQuote.outboundDistanceKm
                            ).toFixed(1)} km`
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        Outbound billable km
                      </span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? `${Number(
                              deliveryQuote.outboundBillableKm
                            ).toFixed(1)} km`
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>Outbound fee</span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? formatMoney(
                              deliveryQuote.outboundFee
                            )
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>Return distance</span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? `${Number(
                              deliveryQuote.returnDistanceKm
                            ).toFixed(1)} km`
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        Return billable km
                      </span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? `${Number(
                              deliveryQuote.returnBillableKm
                            ).toFixed(1)} km`
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>Return fee</span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? formatMoney(
                              deliveryQuote.returnFee
                            )
                          : "—"}
                      </div>
                    </div>

                    <div className="memKV">
                      <span>
                        Total delivery fee
                      </span>
                      <div className="memKV__v">
                        {deliveryFeeCalculated
                          ? formatMoney(
                              deliveryQuote.deliveryFee
                            )
                          : "—"}
                      </div>
                    </div>

                    <div className="memMuted">
                      Explore: 0 free trips, all km
                      billable. Adventure: 4 free trips,
                      $2/km after 50 km per trip. Elite:
                      8 free trips, $2/km after 100 km
                      per trip.
                    </div>
                  </div>
                </>
              )}

              <div className="memFormRow">
                <div className="memLabel">
                  Customer Email
                </div>

                <div
                  className="memInfoBlock"
                  style={{ padding: 14 }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      cursor: selectedContact?.email
                        ? "pointer"
                        : "not-allowed",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sendConfirmationEmail}
                      onChange={(e) => {
                        setSendConfirmationEmail(
                          e.target.checked
                        );
                        setEmailStatus(null);
                        setSaveErr(null);
                      }}
                      disabled={
                        !selectedContact?.email ||
                        saving
                      }
                      style={{ marginTop: 3 }}
                    />

                    <span>
                      <strong>
                        Send booking confirmation email
                      </strong>

                      <div
                        className="memMuted"
                        style={{ marginTop: 4 }}
                      >
                        {selectedContact?.email
                          ? `The confirmation will be sent to ${selectedContact.email}.`
                          : "This customer does not have an email address."}
                      </div>
                    </span>
                  </label>
                </div>
              </div>

              <div className="memFormRow">
                <div className="memLabel">Notes</div>

                <input
                  className="memInput"
                  value={notes}
                  onChange={(e) =>
                    setNotes(e.target.value)
                  }
                  placeholder="Add-ons, timing, special requests…"
                />
              </div>

              <div className="memStatusRow">
                <div className="memStatus">
                  <div className="memLabel">Days</div>

                  <div className="memBig">
                    {typeof days === "number"
                      ? days
                      : "—"}
                  </div>
                </div>

                <div className="memStatus">
                  <div className="memLabel">
                    Conflict
                  </div>

                  {conflictInfo.conflict ? (
                    <Badge tone="danger">
                      ❌ Conflict
                    </Badge>
                  ) : (
                    <Badge tone="success">
                      ✓ Clear
                    </Badge>
                  )}

                  {conflictInfo.conflict && (
                    <div
                      className="memMuted"
                      style={{ marginTop: 8 }}
                    >
                      Conflicts with:

                      <ul
                        style={{
                          margin: "6px 0 0 18px",
                        }}
                      >
                        {conflictInfo.blockers.map(
                          (b) => (
                            <li key={b.id}>
                              {String(b.id).slice(0, 8)}
                              : {b.start_date} →{" "}
                              {b.end_date} ({b.status})
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {emailStatus && (
                <div
                  style={{
                    marginTop: 12,
                    ...emailStatusStyle,
                  }}
                >
                  {emailStatus.message}
                </div>
              )}

              {saveErr && (
                <div
                  className="adminAuth__error"
                  style={{ marginTop: 12 }}
                >
                  {saveErr}
                </div>
              )}

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="btn btn--primary"
                  onClick={submit}
                  disabled={
                    saving ||
                    loading ||
                    !selectedMembership ||
                    !vehicleId ||
                    !startDate ||
                    !endDate ||
                    days === null ||
                    days < 1 ||
                    conflictInfo.conflict ||
                    !canBook ||
                    (sendConfirmationEmail &&
                      !selectedContact?.email?.trim()) ||
                    (serviceType === "delivery" &&
                      !deliveryAddress.trim()) ||
                    (serviceType === "delivery" &&
                      !deliveryFeeCalculated)
                  }
                  type="button"
                >
                  {saving
                    ? sendConfirmationEmail
                      ? "Creating Booking…"
                      : "Saving…"
                    : sendConfirmationEmail
                    ? "Create Booking & Send Email"
                    : "Create Booking"}
                </button>

                <Link
                  className="link"
                  to="/admin/bookings"
                >
                  Cancel
                </Link>
              </div>

              <div
                className="memMuted"
                style={{ marginTop: 10 }}
              >
                This creates a row in <b>bookings</b>{" "}
                and a matching row in{" "}
                <b>booking_delivery</b>. When enabled,
                the page then calls the{" "}
                <b>send-booking-confirmation</b>{" "}
                Supabase Edge Function.
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}